// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the Khipus an account keeps on AYNI's server (`/api/khipus`;
 * `functions/api/khipus/`): list, save, read, delete. Only for a plan
 * that keeps Khipus (`khipuRetention`); Free accounts download only. Each
 * Khipu says until when it is kept (`keptUntil`, epoch ms). After the
 * licence, the list comes back `readOnly` until the last date passes:
 * read and delete, no saving.
 *
 * AYNI (2026-11-03, playlists retire for Khipus): a successful save or
 * delete tells the page (`onKhipusChanged`), so My Khipus and the dataset
 * menu follow a change made elsewhere, and the last list fetched stays at
 * hand (`lastKeptList`) for the dataset menu, which draws at once.
 */
import type { Khipu } from './khipu'

export interface KeptKhipu {
  id: string
  kind: string
  slug: string | null
  title: string
  bytes: number
  createdAt: number
  savedAt: number
  keptUntil: number
  files: number
  /** For a story or a briefing: the app datasets it names (the dataset menu's ticks). */
  datasets?: string[]
}

const CHANGE_EVENT = 'ayni-khipus:change'
const changes: EventTarget = typeof window === 'undefined' ? new EventTarget() : window

/** Something saved or deleted a kept Khipu (its id). */
export function onKhipusChanged(listener: (id: string) => void): () => void {
  const handler = (e: Event) => listener((e as CustomEvent<string>).detail)
  changes.addEventListener(CHANGE_EVENT, handler)
  return () => changes.removeEventListener(CHANGE_EVENT, handler)
}

function changed(id: string): void {
  lastList = null
  changes.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: id }))
}

/** The last list `listKeptKhipus` fetched, until a change or `forgetKeptList`. */
let lastList: KeptList | null = null

export function lastKeptList(): KeptList | null {
  return lastList
}

/** Forget the last list (another account). */
export function forgetKeptList(): void {
  lastList = null
}

export type KhipuStoreError =
  | 'signed_out'
  | 'plan_required'
  | 'not_configured'
  | 'not_found'
  | 'too_large'
  | 'too_many'
  | 'full'
  | 'invalid'
  | 'unavailable'

export type StoreResult<T> = { ok: true; value: T } | { ok: false; error: KhipuStoreError }

async function errorOf(res: Response): Promise<KhipuStoreError> {
  if (res.status === 401) return 'signed_out'
  if (res.status === 402) return 'plan_required'
  if (res.status === 503) return 'not_configured'
  if (res.status === 404) return 'not_found'
  if (res.status === 413) return 'too_large'
  if (res.status === 409) {
    const body = (await res.json().catch(() => null)) as { error?: unknown } | null
    return body?.error === 'storage_full' ? 'full' : 'too_many'
  }
  if (res.status === 400) return 'invalid'
  return 'unavailable'
}

async function request<T>(fetchImpl: typeof fetch, url: string, init: RequestInit, read: (body: unknown) => T): Promise<StoreResult<T>> {
  try {
    const res = await fetchImpl(url, { credentials: 'same-origin', ...init, headers: { Accept: 'application/json', ...(init.headers ?? {}) } })
    if (!res.ok) return { ok: false, error: await errorOf(res) }
    return { ok: true, value: read(await res.json()) }
  } catch {
    return { ok: false, error: 'unavailable' }
  }
}

export interface KeptList {
  items: KeptKhipu[]
  /** The plan no longer keeps Khipus: these are the ones left until their date. */
  readOnly: boolean
}

export async function listKeptKhipus(fetchImpl: typeof fetch = fetch): Promise<StoreResult<KeptList>> {
  const result = await request(fetchImpl, '/api/khipus', { method: 'GET' }, (b) => {
    const body = b as { khipus: KeptKhipu[]; retention?: unknown }
    return { items: body.khipus, readOnly: body.retention === null }
  })
  lastList = result.ok ? result.value : null
  return result
}

export async function saveKhipu(k: Khipu, files: Array<{ name: string; body: string }> = [], fetchImpl: typeof fetch = fetch): Promise<StoreResult<KeptKhipu>> {
  const result = await request(fetchImpl, '/api/khipus', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ khipu: k, files }),
  }, (b) => (b as { khipu: KeptKhipu }).khipu)
  if (result.ok) changed(k.id)
  return result
}

export function readKeptKhipu(id: string, fetchImpl: typeof fetch = fetch): Promise<StoreResult<{ khipu: Khipu; files: Array<{ name: string; body: string }>; keptUntil: number }>> {
  return request(fetchImpl, `/api/khipus/${encodeURIComponent(id)}`, { method: 'GET' }, (b) => b as { khipu: Khipu; files: Array<{ name: string; body: string }>; keptUntil: number })
}

export async function deleteKeptKhipu(id: string, fetchImpl: typeof fetch = fetch): Promise<StoreResult<true>> {
  const result = await request(fetchImpl, `/api/khipus/${encodeURIComponent(id)}`, { method: 'DELETE' }, () => true as const)
  if (result.ok) changed(id)
  return result
}
